import * as React from "react";
import { Drawer, DrawerContent } from "../../../components/ui/drawer";
import { Restaurant } from "../../../types/restaurant";
import { RestaurantCard } from "../../../components/RestaurantCard";

interface RestaurantListDrawerProps {
  restaurants: Restaurant[];
  isOpen: boolean;
  onClose: () => void;
  onRestaurantSelect: (
    restaurant: Restaurant,
    mode: "takeaway" | "booking"
  ) => void;
  userLocation: { latitude: number; longitude: number } | null;
  isRouteFiltered?: boolean;
  routeDistances?: Map<string, number>;
  isCalculatingDistances?: boolean;
}

interface RestaurantWithDistance extends Restaurant {
  distance: number;
  durationText: string;
}

/**
 * Calculate distance between two coordinates using Haversine formula
 */
const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const RestaurantListDrawer: React.FC<RestaurantListDrawerProps> = ({
  restaurants,
  isOpen,
  onClose,
  onRestaurantSelect,
  userLocation,
  isRouteFiltered = false,
  routeDistances,
  isCalculatingDistances = false,
}) => {
  const [activeTab, setActiveTab] = React.useState<"takeaway" | "booking">(
    "takeaway"
  );

  // Sort restaurants by distance
  // If route is selected, use distance from route; otherwise use distance from user location
  const sortedRestaurants = React.useMemo(() => {
    // If route is filtered and we have route distances, use those
    if (isRouteFiltered && routeDistances && routeDistances.size > 0) {
      return [...restaurants]
        .map(
          (restaurant): RestaurantWithDistance => ({
            ...restaurant,
            distance: routeDistances.get(restaurant.dbID) || 0,
            durationText: "",
          })
        )
        .sort((a, b) => a.distance - b.distance);
    }

    // Otherwise, use distance from user location if available
    if (!userLocation) return restaurants as RestaurantWithDistance[];

    return [...restaurants]
      .map((restaurant): RestaurantWithDistance => {
        // Use existing distance if already calculated accurately (e.g. from Firebase Function)
        const hasDistance = (restaurant as any).distance !== undefined;
        const distance = hasDistance
          ? (restaurant as any).distance
          : calculateDistance(
              userLocation.latitude,
              userLocation.longitude,
              restaurant.storeLat,
              restaurant.storeLong
            );

        return {
          ...restaurant,
          distance,
          durationText: (restaurant as any).durationText || "",
        };
      })
      .sort((a, b) => a.distance - b.distance);
  }, [restaurants, userLocation, isRouteFiltered, routeDistances]);

  // Filter restaurants based on active tab
  const filteredRestaurants = React.useMemo(() => {
    if (activeTab === "takeaway") {
      return sortedRestaurants;
    }
    return sortedRestaurants.filter(
      (restaurant) => restaurant.isTableBooking === true
    );
  }, [sortedRestaurants, activeTab]);

  console.log({ filteredRestaurants });

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent
        className="h-[85vh] bg-[#1a1a1a] border-t border-gray-800"
        aria-label="Nearby restaurants list"
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-gray-800 flex-shrink-0">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                {isRouteFiltered && (
                  <svg
                    className="w-5 h-5 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                    />
                  </svg>
                )}
                {isRouteFiltered
                  ? "Restaurants Along Route"
                  : "Nearby Restaurants"}
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                {filteredRestaurants.length} restaurant
                {filteredRestaurants.length !== 1 ? "s" : ""} found
                {isRouteFiltered && (
                  <span className="ml-1 text-green-400">
                    • Within 10km of your route
                  </span>
                )}
                {!isRouteFiltered && isCalculatingDistances && (
                  <span className="ml-1 text-orange-400 animate-pulse">
                    • Calculating accurate distances...
                  </span>
                )}
              </p>
            </div>

            {/* Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab("takeaway")}
                className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-colors ${
                  activeTab === "takeaway"
                    ? "bg-orange-500 text-white"
                    : "bg-white/5 text-gray-400 hover:bg-white/10"
                }`}
              >
                Take Away
              </button>
              <button
                onClick={() => setActiveTab("booking")}
                className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-colors ${
                  activeTab === "booking"
                    ? "bg-orange-500 text-white"
                    : "bg-white/5 text-gray-400 hover:bg-white/10"
                }`}
              >
                Table Booking
              </button>
            </div>
          </div>

          {/* Restaurant List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
            {filteredRestaurants.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <p className="text-gray-400 text-base">
                  {activeTab === "booking"
                    ? "No restaurants with table booking available"
                    : "No restaurants found"}
                </p>
              </div>
            ) : (
              filteredRestaurants.map((restaurant) => (
                <RestaurantCard
                  key={restaurant.dbID}
                  restaurant={restaurant}
                  onClick={() => {
                    onRestaurantSelect(restaurant, activeTab);
                    onClose();
                  }}
                  showRouteIndicator={
                    isRouteFiltered &&
                    routeDistances !== undefined &&
                    routeDistances.size > 0
                  }
                />
              ))
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
